import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box, Button, CircularProgress, Container,
  Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Paper, Stack, TextField, Typography,
} from "@mui/material";
import { MdAdd, MdDelete, MdRefresh, MdSearch } from "react-icons/md";
import { getAllNotifications } from "../store/actions/notifications";
import { deleteNotification } from "../store/actions/signals";
import AddNotificationsDialog from "../components/modals/AddNotificationsDialog";
import Colors from "../../assets/styles/Colors";

function fmtDate(v) {
  if (!v?.seconds) return "—";
  const d = new Date(v.seconds * 1000 + (v.nanoseconds ?? 0) / 1e6);
  return isNaN(d) ? "—" : d.toLocaleDateString();
}

export default function NotificationsView() {
  const dispatch = useDispatch();
  const { notifications } = useSelector((s) => s.notifications);
  const { isSuperAdmin, isAlmighty } = useSelector((s) => s.auth);

  const isFirstUse = useRef(true);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState("");
  const [addOpen, setAddOpen] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(null); // { id, title }
  const [deleting, setDeleting]           = useState(false);

  const loadNotifications = useCallback(() => {
    setLoading(true);
    dispatch(getAllNotifications());
    setTimeout(() => setLoading(false), 800);
  }, [dispatch]);

  useEffect(() => {
    if (isFirstUse.current) {
      isFirstUse.current = false;
      loadNotifications();
    }
  }, [loadNotifications]);

  const list = notifications || [];

  const filtered = (() => {
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter((n) =>
      (n.notification?.title || "").toLowerCase().includes(q) ||
      (n.notification?.body  || "").toLowerCase().includes(q)
    );
  })();

  const confirmDeleteAction = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await deleteNotification(confirmDelete.id);
      setConfirmDelete(null);
      loadNotifications();
    } catch (err) {
      console.error("Error deleting notification:", err);
    } finally {
      setDeleting(false);
    }
  };

  const canDelete = isSuperAdmin || isAlmighty;

  return (
    <Box sx={{ width: "100%", py: 3 }}>
      <Container maxWidth="xl">

        {/* Header */}
        <Paper sx={{ mb: 3, p: 2.5, borderRadius: 3, background: `linear-gradient(135deg, ${Colors.bgSecondary} 0%, ${Colors.bgTertiary} 100%)`, border: `1px solid ${Colors.borderSecondary}` }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h5" sx={{ color: Colors.textPrimary }}>Notificaciones Push</Typography>
              <Typography variant="body2" sx={{ color: Colors.textSecondary }}>
                Historial de notificaciones enviadas a los usuarios
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.5}>
              <IconButton onClick={loadNotifications} sx={{ color: Colors.textSecondary }}>
                <MdRefresh size={20} />
              </IconButton>
              <Button
                variant="contained"
                startIcon={<MdAdd />}
                onClick={() => setAddOpen(true)}
                sx={{ backgroundColor: Colors.primary, color: Colors.white, textTransform: "none", borderRadius: 2 }}
              >
                Nueva Notificación
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {/* Search */}
        <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
          <TextField
            size="small"
            placeholder="Buscar notificación..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <MdSearch style={{ marginRight: 6, color: Colors.textSecondary }} /> }}
            sx={{
              minWidth: 300,
              "& .MuiOutlinedInput-root": {
                backgroundColor: Colors.bgSecondary,
                color: Colors.textPrimary,
                "& fieldset": { borderColor: Colors.borderSecondary },
                "&:hover fieldset": { borderColor: Colors.borderPrimary },
              },
            }}
          />
          <Typography variant="caption" sx={{ color: Colors.textSecondary }}>
            {filtered.length} {filtered.length === 1 ? "notificación" : "notificaciones"}
          </Typography>
        </Stack>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: Colors.primary }} />
          </Box>
        )}

        {!loading && (
          <Paper sx={{ borderRadius: 3, backgroundColor: Colors.bgSecondary, border: `1px solid ${Colors.borderSecondary}`, overflow: "hidden" }}>
            {/* Table header */}
            <Box sx={{ px: 3, py: 1.5, backgroundColor: Colors.bgTertiary, borderBottom: `1px solid ${Colors.borderSecondary}` }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ flex: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: Colors.textSecondary }}>Título</Typography>
                </Box>
                <Box sx={{ flex: 3 }}>
                  <Typography variant="subtitle2" sx={{ color: Colors.textSecondary }}>Descripción</Typography>
                </Box>
                <Box sx={{ width: 120, flexShrink: 0 }}>
                  <Typography variant="subtitle2" sx={{ color: Colors.textSecondary }}>Canal / Origen</Typography>
                </Box>
                <Box sx={{ width: 110, flexShrink: 0 }}>
                  <Typography variant="subtitle2" sx={{ color: Colors.textSecondary }}>Fecha</Typography>
                </Box>
                <Box sx={{ width: 70, flexShrink: 0 }}>
                  <Typography variant="subtitle2" sx={{ color: Colors.textSecondary }}>Eliminar</Typography>
                </Box>
              </Stack>
            </Box>

            {filtered.length === 0 && (
              <Box sx={{ px: 3, py: 4, textAlign: "center" }}>
                <Typography sx={{ color: Colors.textSecondary }}>Sin notificaciones.</Typography>
              </Box>
            )}

            {filtered.map((notif, idx) => {
              const title  = notif.notification?.title || "—";
              const body   = notif.notification?.body  || "—";
              const source = notif.channelName || (notif.source === "telegram" ? "Telegram" : null) || "—";
              return (
                <Box
                  key={notif.id || idx}
                  sx={{
                    px: 3, py: 1.75,
                    borderBottom: `1px solid ${Colors.borderSecondary}`,
                    "&:hover": { backgroundColor: Colors.bgTertiary },
                    transition: "background-color 0.15s",
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ flex: 2 }}>
                      <Typography variant="body2" sx={{ color: Colors.textPrimary, fontWeight: 600 }}>{title}</Typography>
                    </Box>
                    <Box sx={{ flex: 3, overflow: "hidden" }}>
                      <Typography
                        variant="caption"
                        sx={{ color: Colors.textSecondary, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      >
                        {body}
                      </Typography>
                    </Box>
                    <Box sx={{ width: 120, flexShrink: 0 }}>
                      <Typography variant="caption" sx={{ color: Colors.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
                        {source}
                      </Typography>
                    </Box>
                    <Box sx={{ width: 110, flexShrink: 0 }}>
                      <Typography variant="caption" sx={{ color: Colors.textSecondary }}>{fmtDate(notif.cdate)}</Typography>
                    </Box>
                    <Box sx={{ width: 70, flexShrink: 0 }}>
                      <IconButton
                        size="small"
                        onClick={() => setConfirmDelete({ id: notif.id, title })}
                        disabled={!canDelete}
                        sx={{ color: Colors.error }}
                        title="Eliminar"
                      >
                        <MdDelete size={16} />
                      </IconButton>
                    </Box>
                  </Stack>
                </Box>
              );
            })}
          </Paper>
        )}

        {/* Dialogs */}
        <AddNotificationsDialog open={addOpen} onClose={() => setAddOpen(false)} />

        {/* Confirm delete dialog */}
        <Dialog
          open={!!confirmDelete}
          onClose={() => !deleting && setConfirmDelete(null)}
          PaperProps={{ sx: { backgroundColor: Colors.bgSecondary, border: `1px solid ${Colors.borderSecondary}` } }}
        >
          <DialogTitle sx={{ color: Colors.textPrimary }}>Confirmar eliminación</DialogTitle>
          <DialogContent>
            <Typography sx={{ color: Colors.textSecondary }}>
              ¿Eliminar la notificación "{confirmDelete?.title}"? Esta acción no se puede deshacer.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDelete(null)} disabled={deleting} sx={{ color: Colors.textSecondary }}>
              Cancelar
            </Button>
            <Button onClick={confirmDeleteAction} disabled={deleting} sx={{ color: Colors.error }}>
              {deleting ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogActions>
        </Dialog>

      </Container>
    </Box>
  );
}
